import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { get, post } from "../../api/api";
import MaterialTable from "@material-table/core";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { Check } from "@mui/icons-material";
import { MobilePOSActivationType, Tenant } from "../../models/Tenant";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";

import useStyles from "./styles";
import { Alert } from "@material-ui/lab";
import { Authenticated, hasRole } from "../../models/User";
import { UserContext } from "../../UserProvider";
import { useI18n } from "../../hooks/useI18n";

const mapActivationType = (type: string): number | undefined => {
  const mapping: Record<string, number> = {
    "": MobilePOSActivationType.NONE,
    KRIS: MobilePOSActivationType.KRIS,
    KRIS_HIGH: MobilePOSActivationType.KRIS_HIGH
  };

  return mapping[type];
};

const fetchTenantChainStores = async (tenantChainId: string) => {
  const response = await get(
    `/v1/mpos/tenant-chains-stores?tenantChainId=${tenantChainId}`
  );

  const data = await response.json();
  return data;
};

export const activateStoreMPOS = async (payload: any) => {
  const activationType = mapActivationType(payload.activationType);
  debugger;
  const response = await post(
    `/v1/mpos/toggle-store-mobile-pos-activation?activate=${payload.activate}&tenantId=${payload.id}&activationType=${activationType}`
  );
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail);
  }

  const data = await response.json();
  return data;
};

interface MobilePOSChainStoreListProps {
  tenantChainId: string;
}

const MobilePOSStoreList: React.FC<MobilePOSChainStoreListProps> = ({
  tenantChainId
}) => {
  const classes = useStyles();
  const user = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const { data, isLoading, isError } = useQuery(
    ["tenantChainStores", tenantChainId],
    () => fetchTenantChainStores(tenantChainId),
    {
      staleTime: Infinity
    }
  );

  const [showStartMobilePOS, setShowStartMobilePOS] = useState(false);

  const [selectedStore, setSelectedStore] = useState({
    id: "",
    name: "",
    activate: false
  });

  const [selectedOption, setSelectedOption] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleStartClick = () => {
    setConfirmDialogOpen(true);
  };

  const [activateError, setActivateError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { mutate, isLoading: startRequestLoading } = useMutation(
    activateStoreMPOS,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["tenantChainStores", tenantChainId]);
        setConfirmDialogOpen(false);
        setShowStartMobilePOS(false);
      },
      onError: (err: Error | any) => {
        console.error("POST activate request failed:", err);
        setActivateError(
          err.message || "Failed to activate. Please try again."
        );
      }
    }
  );

  return (
    <div className={[classes.tableWrapper, classes.detailTable].join(" ")}>
      <MaterialTable
        isLoading={isLoading}
        style={{ boxShadow: "none", backgroundColor: "#eee" }}
        title=""
        columns={[
          {
            title: "Id",
            field: "customerId",
            headerStyle: { backgroundColor: "#eee" }
          },
          {
            title: t("common.tableHeaders.store"),
            field: "name",
            headerStyle: { backgroundColor: "#eee" }
          },
          {
            title: t("common.formFields.activationType"),
            field: "mobilePOSActivationType",
            headerStyle: { backgroundColor: "#eee" },
            render: (rowData: Tenant) => {
              if (rowData.mobilePOSActive === true) {
                return rowData.mobilePOSActivationType;
              }
              return "";
            }
          },
          {
            render: (rowData: Tenant) => (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {rowData.mobilePOSActive === true ? (
                  <Button
                    startIcon={<Check />}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedStore({
                        id: rowData.id,
                        name: rowData.name,
                        activate: false
                      });
                      setShowStartMobilePOS(true);
                    }}
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    startIcon={<Check />}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedStore({
                        id: rowData.id,
                        name: rowData.name,
                        activate: true
                      });
                      setShowStartMobilePOS(true);
                    }}
                  >
                    Start
                  </Button>
                )}
              </div>
            ),
            cellStyle: { textAlign: "right" },
            headerStyle: { textAlign: "right", backgroundColor: "#eee" }
          }
        ]}
        data={data}
      />

      <Dialog
        fullWidth
        maxWidth={"sm"}
        open={showStartMobilePOS}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          {`${
            selectedStore.activate
              ? t("common.messages.start")
              : t("common.messages.stop")
          } Mobile POS in ${selectedStore.name}`}
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setSelectedStore({ id: "", name: "", activate: false });
              setShowStartMobilePOS(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent dividers>
          {!selectedStore.activate ? (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  startIcon={<Check />}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleStartClick}
                >
                  Stop
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="mobile-pos"
                    name="mobile-pos"
                    value={selectedOption}
                    onChange={e => setSelectedOption(e.target.value)}
                  >
                    <FormControlLabel
                      value="KRIS"
                      control={<Radio />}
                      label={t("common.messages.kris")}
                    />
                    {hasRole(user, ["ITAdmin"]) && (
                      <FormControlLabel
                        value="KRIS_HIGH"
                        control={<Radio />}
                        label={t("common.messages.krisHigh")}
                      />
                    )}
                  </RadioGroup>
                </FormControl>

                <Button
                  startIcon={<Check />}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleStartClick}
                  disabled={!selectedOption}
                >
                  Start
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogContent>
          {`Are you sure you want ${
            selectedStore.activate ? "start" : "stop"
          } mobile POS`}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false), setActivateError("");
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              mutate({
                id: selectedStore.id,
                activationType: selectedOption,
                activate: selectedStore.activate
              });
            }}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
        {activateError && (
          <Box sx={{ marginTop: 2, padding: 2 }}>
            <Alert severity="error">{activateError}</Alert>
          </Box>
        )}
      </Dialog>
    </div>
  );
};

export default MobilePOSStoreList;
